import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { OpenGuideSource } from "@expert/solve-tools";
import { guidedFlowRecommendationMap, setRecommendation, useGuideStore } from "@expert/solve-tools";
import { addGuideRecommendationMessage } from "@soluto-private/expert-workspace-timeline";
import { useEffect } from "react";
import { getRootDispatcher } from "@/analytics";

export const useGuideRecommendation = ({
    isGuideOpen,
    logger,
    openGuide,
    sessionId,
}: {
    isGuideOpen: boolean;
    logger: Logger;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
    sessionId: string;
}) => {
    const rootDispatcher = getRootDispatcher();

    const {
        features: { isGuidedFlowRecommendationEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { availableGuides, disableAutomaticOpen, recommendation } = useGuideStore();

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (!isGuidedFlowRecommendationEnabled) return;
        if (!isGuideOpen && !disableAutomaticOpen && recommendation) {
            logger.info({ recommendation }, `Solve Guide | auto opening guide for recommendation`);

            const guideToAutoOpen = guidedFlowRecommendationMap[recommendation];
            if (guideToAutoOpen && availableGuides.map((guide) => guide.title)?.includes(guideToAutoOpen)) {
                void rootDispatcher.dispatchBusinessEvent("Guide_RecommendationMessageShown", {
                    guide: guideToAutoOpen,
                });
                trackFullStoryEvent("Guide_RecommendationMessageShown", { guide: guideToAutoOpen });
                addGuideRecommendationMessage(guideToAutoOpen, sessionId);
                openGuide("recommendation", guideToAutoOpen);
            }
        } else if (recommendation) {
            logger.info(
                { disableAutomaticOpen, recommendation },
                `Solve Guide | recommendation received, but not opening guide`,
            );
        }
        setRecommendation(logger, undefined);
    }, [isGuideOpen, recommendation, setRecommendation]);
};
